import type {
  DailyPayEntry,
  DailyPayRow,
} from '@/app/features/employee/emp-payroll';
import CustomPagination from '@/components/CustomPagination';
import EmptyState from '@/components/EmptyState';
import { UsersIcon } from '@/icons';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/utils';
import { Skeleton, Tooltip } from 'antd';
import type { Dayjs } from 'dayjs';
import { memo } from 'react';
import {
  CELL_LABEL,
  CELL_STYLE,
  CELL_STYLE_DIRTY,
  toCellState,
  type CellState,
} from './pay-sheet.helpers';

const DayCell = memo(
  ({
    state,
    locked,
    dirty,
    editing,
    onClick,
  }: {
    state: CellState;
    locked?: boolean;
    dirty?: boolean;
    editing?: boolean;
    onClick: () => void;
  }) => {
    const disabled = locked || !editing;

    const button = (
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={cn(
          'h-10 w-full rounded-md border text-sm font-semibold transition-colors',
          dirty ? CELL_STYLE_DIRTY[state] : CELL_STYLE[state],
          locked && 'opacity-40 cursor-not-allowed',
          !locked && !editing && 'cursor-default',
          !disabled && 'hover:border-theme-primary cursor-pointer',
        )}
      >
        {CELL_LABEL[state]}
      </button>
    );

    if (!locked) return button;
    return (
      <Tooltip title="Locked — salary sheet already generated">
        <span className="block">{button}</span>
      </Tooltip>
    );
  },
);
DayCell.displayName = 'DayCell';

type Props = {
  days: Dayjs[];
  rows: DailyPayRow[];
  loading?: boolean;
  editing?: boolean;
  page: number;
  limit: number;
  total: number;
  /** Local unsaved edits, keyed `user_id|YYYY-MM-DD`. */
  pending: Record<string, boolean>;
  onToggleCell: (row: DailyPayRow, entry: DailyPayEntry) => void;
  onSetColumn: (date: string, value: boolean) => void;
  onPaginate: (page: number, limit: number) => void;
};

const PaySheetGrid = ({
  days,
  rows,
  loading,
  editing,
  page,
  limit,
  total,
  pending,
  onToggleCell,
  onSetColumn,
  onPaginate,
}: Props) => {
  const gridTemplate = `repeat(${days.length}, minmax(46px, 1fr))`;

  const resolveState = (row: DailyPayRow, entry: DailyPayEntry): CellState => {
    const key = `${row.user_id}|${entry.date}`;
    if (key in pending) return toCellState(pending[key]);
    return toCellState(entry.is_payable);
  };

  const isDirty = (row: DailyPayRow, entry: DailyPayEntry) =>
    `${row.user_id}|${entry.date}` in pending;

  const payableCount = (row: DailyPayRow) =>
    row.entries.filter((entry) => resolveState(row, entry) === 'PAYABLE').length;

  if (!loading && !rows.length) {
    return (
      <EmptyState
        icon={<UsersIcon size={32} className="text-theme-primary" />}
        title="No daily pay employee found"
        description="Employees with pay type Daily will show up here once they are onboarded."
      />
    );
  }

  return (
    <div className="bg-theme-slate-800 border border-theme-gray-700 rounded-xl p-4">
      <div className="overflow-x-auto">
        <div className="min-w-[1100px]">
          {/* header — never skeletonised */}
          <div className="flex gap-3 sticky top-0 z-10 bg-theme-slate-800 pb-3">
            <div className="w-[280px] shrink-0 text-white/60 text-sm self-end pb-2">
              Employee
            </div>
            <div
              className="flex-1 grid gap-1.5"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              {days.map((day) => (
                <button
                  key={day.format('YYYY-MM-DD')}
                  type="button"
                  disabled={!editing}
                  title={
                    editing
                      ? 'Mark this day payable for everyone on this page'
                      : undefined
                  }
                  onClick={() => onSetColumn(day.format('YYYY-MM-DD'), true)}
                  className={cn(
                    'text-center rounded-md py-1 transition-colors',
                    editing
                      ? 'hover:bg-theme-slate-900 cursor-pointer'
                      : 'cursor-default',
                  )}
                >
                  <div className="text-white text-sm font-medium leading-4">
                    {day.format('DD')}
                  </div>
                  <div className="text-white/40 text-[11px] leading-4">
                    {day.format('ddd')}
                  </div>
                </button>
              ))}
            </div>
            <div className="w-[170px] shrink-0 text-white/60 text-sm self-end pb-2 text-right">
              Total
            </div>
          </div>

          {/* rows */}
          <div className="space-y-2">
            {loading
              ? Array.from({ length: limit }).map((_, index) => (
                  <div
                    key={index}
                    className="flex gap-3 items-center border-b border-theme-gray-700/60 pb-2 last:border-0"
                  >
                    <div className="w-[280px] shrink-0 space-y-1.5">
                      <Skeleton.Input active block className="!h-4 !min-w-0" />
                      <Skeleton.Input
                        active
                        block
                        className="!h-3 !min-w-0 !w-1/2"
                      />
                    </div>
                    <div
                      className="flex-1 grid gap-1.5"
                      style={{ gridTemplateColumns: gridTemplate }}
                    >
                      {days.map((day) => (
                        <Skeleton.Button
                          key={day.format('YYYY-MM-DD')}
                          active
                          block
                          className="!h-10 !min-w-0"
                        />
                      ))}
                    </div>
                    <div className="w-[170px] shrink-0 space-y-1.5">
                      <Skeleton.Input active block className="!h-4 !min-w-0" />
                      <Skeleton.Input
                        active
                        block
                        className="!h-3 !min-w-0 !w-2/3"
                      />
                    </div>
                  </div>
                ))
              : rows.map((row) => {
                  const daysPaid = payableCount(row);
                  const rate = Number(row.daily_rate || 0);
                  return (
                    <div
                      key={row.user_id}
                      className="flex gap-3 items-center border-b border-theme-gray-700/60 pb-2 last:border-0"
                    >
                      <div className="w-[280px] shrink-0">
                        <div className="text-white text-sm font-medium">
                          {row.name}
                        </div>
                        <div className="text-white/40 text-xs">
                          {row.employee_id}
                          {row.department_name
                            ? ` · ${row.department_name}`
                            : ''}
                        </div>
                      </div>

                      <div
                        className="flex-1 grid gap-1.5"
                        style={{ gridTemplateColumns: gridTemplate }}
                      >
                        {row.entries.map((entry) => (
                          <DayCell
                            key={entry.date}
                            state={resolveState(row, entry)}
                            dirty={isDirty(row, entry)}
                            editing={editing}
                            onClick={() => onToggleCell(row, entry)}
                          />
                        ))}
                      </div>

                      <div className="w-[170px] shrink-0 text-right">
                        <div className="text-white text-sm font-semibold">
                          {formatNumber(daysPaid * rate, { currency: 'BDT' })}
                        </div>
                        <div className="text-white/40 text-xs">
                          {daysPaid} days × {formatNumber(rate)}
                        </div>
                      </div>
                    </div>
                  );
                })}
          </div>
        </div>
      </div>

      {/* footer — never skeletonised */}
      {total > 0 && (
        <div className="mt-4">
          <CustomPagination
            page={page}
            limit={limit}
            total={total}
            handlePagination={onPaginate}
          />
        </div>
      )}
    </div>
  );
};

export default PaySheetGrid;
